/**
 * <s-roof-bar-lookup> narrows the roof bar fitment list by make, then model description, then car type.
 * Liquid passes the fitment data asset URL in `data-src`; each row is
 * [make, model description, car type, series code, doors, roof type, sku, colour, material, length].
 * Choosing the "not listed" option at any step, or a combination with no rows, shows the apology notice.
 */
const NOT_LISTED = '__not_listed__';
const DETAIL_FIELDS = ['series', 'doors', 'roof', 'sku', 'colour', 'material', 'length'];

class RoofBarLookupElement extends HTMLElement {
  #rows = [];
  #controller = null;

  async connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.make = this.querySelector('[data-lookup-make]');
    this.model = this.querySelector('[data-lookup-model]');
    this.type = this.querySelector('[data-lookup-type]');
    this.results = this.querySelector('[data-lookup-results]');
    this.list = this.querySelector('[data-lookup-list]');
    this.empty = this.querySelector('[data-lookup-empty]');
    this.template = this.querySelector('[data-lookup-template]');

    this.make.addEventListener('change', () => this.#onMake(), { signal });
    this.model.addEventListener('change', () => this.#onModel(), { signal });
    this.type.addEventListener('change', () => this.#render(), { signal });

    try {
      const response = await fetch(this.dataset.src, { signal });
      this.#rows = await response.json();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#rows = [];
    }

    this.#fill(this.make, this.#unique(this.#rows, 0));
    this.make.disabled = false;
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  #unique(rows, index) {
    return [...new Set(rows.map((row) => row[index]).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  #fill(select, values) {
    const placeholder = select.querySelector('option[value=""]');
    const notListed = select.querySelector(`option[value="${NOT_LISTED}"]`);
    select.replaceChildren(placeholder);
    for (const value of values) {
      select.append(new Option(value, value));
    }
    if (notListed) select.append(notListed);
    select.value = '';
  }

  #reset(select) {
    this.#fill(select, []);
    select.disabled = true;
  }

  #onMake() {
    this.#reset(this.model);
    this.#reset(this.type);
    const make = this.make.value;
    if (make && make !== NOT_LISTED) {
      const rows = this.#rows.filter((row) => row[0] === make);
      this.#fill(this.model, this.#unique(rows, 1));
      this.model.disabled = false;
    }
    this.#render();
  }

  #onModel() {
    this.#reset(this.type);
    const model = this.model.value;
    if (model && model !== NOT_LISTED) {
      const rows = this.#rows.filter((row) => row[0] === this.make.value && row[1] === model);
      this.#fill(this.type, this.#unique(rows, 2));
      this.type.disabled = false;
    }
    this.#render();
  }

  #render() {
    const choices = [this.make.value, this.model.value, this.type.value];
    const notListed = choices.includes(NOT_LISTED);
    const complete = choices.every(Boolean);

    this.list.replaceChildren();

    if (notListed) {
      this.results.hidden = true;
      this.empty.hidden = false;
      return;
    }

    if (!complete) {
      this.results.hidden = true;
      this.empty.hidden = true;
      return;
    }

    const matches = this.#rows.filter(
      (row) => row[0] === choices[0] && row[1] === choices[1] && row[2] === choices[2]
    );

    if (matches.length === 0) {
      this.results.hidden = true;
      this.empty.hidden = false;
      return;
    }

    for (const row of matches) {
      const card = this.template.content.cloneNode(true);
      DETAIL_FIELDS.forEach((field, offset) => {
        const slot = card.querySelector(`[data-field="${field}"]`);
        if (slot) slot.textContent = row[offset + 3] || '–';
      });
      const product = this.querySelector(`template[data-sku-product="${CSS.escape(row[6])}"]`);
      if (product) card.querySelector('dl')?.append(product.content.cloneNode(true));
      this.list.append(card);
    }

    this.empty.hidden = true;
    this.results.hidden = false;
  }
}

if (!customElements.get('s-roof-bar-lookup')) {
  customElements.define('s-roof-bar-lookup', RoofBarLookupElement);
}
